import {
    compareAsc,
    add,
    format,
    differenceInMinutes,
    addDays,
    startOfWeek,
    setDate,
    isBefore,
} from 'date-fns';
import fr from 'date-fns/locale/fr';
import type { IntlShape } from 'react-intl';
import type { Range, Time } from '@model/utils';
import type { AgendaSettings } from '@states/agenda';

export type OpeningHours = Range<Time>;

export const localeConfig = {
    locale: fr,
};

export const convertTimeToDate = (hours: number, minutes: number): Date => {
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};

export const roundQuarterHourNearest = (date: Date): Date => {
    const ms = 1000 * 60 * 15;
    const roundedDate = new Date(Math.round(date.getTime() / ms) * ms);
    return roundedDate;
};

export const roundQuarterHourUp = (date: Date): Date => {
    date.setMinutes(date.getMinutes() + 7);
    return roundQuarterHourNearest(date);
};

export const roundQuarterHourDown = (date: Date): Date => {
    date.setMinutes(date.getMinutes() - 7);
    return roundQuarterHourNearest(date);
};

export const accFifteenMinutesUntilConditionPasses = (
    date: Date,
    endDate: Date,
    conditionFn: (d1: Date, d2: Date) => boolean,
    accDates: Date[] = [],
): Date[] =>
    conditionFn(date, endDate)
        ? accDates
        : [
            ...accDates,
            date,
            ...accFifteenMinutesUntilConditionPasses(
                add(date, { minutes: 15 }),
                endDate,
                conditionFn,
                accDates,
            ),
        ];

export const getQuarterHoursInterval = (
    startDate: Date,
    endDate: Date,
): Date[] =>
    accFifteenMinutesUntilConditionPasses(
        startDate,
        endDate,
        (d1, d2) => compareAsc(d1, d2) === 1,
    );

export const getQuarterHoursIntervalFromTime = (
    startHour: number,
    startMinutes: number,
    endHour: number,
    endMinutes: number,
): Date[] => {
    const startDate = roundQuarterHourDown(
        convertTimeToDate(startHour, startMinutes),
    );
    const endDate = roundQuarterHourUp(convertTimeToDate(endHour, endMinutes));
    return getQuarterHoursInterval(startDate, endDate);
};

export const formatDateToHours = (date: Date): string =>
    format(date, 'HH:mm', localeConfig);

export const formatArrayOfDateToHours = (dates: Date[]): string[] =>
    dates.map((date) => formatDateToHours(date));

export const getFormattedHoursIntervalsFromTime = (
    startHour: number,
    startMinutes: number,
    endHour: number,
    endMinutes: number,
): string[] =>
    formatArrayOfDateToHours(
        getQuarterHoursIntervalFromTime(
            startHour,
            startMinutes,
            endHour,
            endMinutes,
        ),
    );

export const differenceInMinutesBetweenTimeAndDate = (
    date: Date,
    { start: { hour, minute } }: Pick<OpeningHours, 'start'>,
): number => differenceInMinutes(date, convertTimeToDate(hour, minute));

export const formatMonth = (date: Date): string =>
    format(date, 'MMMM', localeConfig);

export const convertDateToDayId = (date: Date): number => {
    const year = date.getUTCFullYear();
    const month = `0${date.getUTCMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return Number(`${year}${month}${day}`);
};

export const convertDateToDay = (date: Date): string =>
    format(date, 'EEEE d MMMM', localeConfig);

export const desctructureDayId = (
    dayId: number,
): { year: number; month: number; day: number } => {
    const year = Number(String(dayId).slice(0, 4));
    const month = Number(String(dayId).slice(4, 6));
    const day = Number(String(dayId).slice(6, 8));
    return { year, month, day };
};

export const getDateFromDayId = (dayId: number): Date => {
    const { year, month, day } = desctructureDayId(dayId);
    const date = new Date();
    date.setFullYear(year, month - 1, day);
    return date;
};

export const getDateFromDayIdAndTime = (
    dayId: number,
    hour: number,
    minute: number,
): Date => {
    const { year, month, day } = desctructureDayId(dayId);
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    date.setFullYear(year, month - 1, day);
    return date;
};

export const shortDateFromDayIdAndTime = (
    dayId: number,
    hour: number,
    minute: number,
): string => {
    const date = getDateFromDayIdAndTime(dayId, hour, minute);
    return format(date, 'MMM d HH:mm', localeConfig);
};

export const shortDateFromDayId = (dayId: number): string => {
    const date = getDateFromDayId(dayId);
    return format(date, 'MMM d yyyy', localeConfig);
};

export const getDayRange = (date: Date): { start: Date; end: Date } => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 0, 0);
    return { start, end };
};

export const getRangeFromDayAndHours = (
    date: Date,
    startHour: Date,
    endHour: Date,
): { start: Date; end: Date } => {
    const year = date?.getFullYear() || 0;
    const month = date?.getMonth() || 0;
    const day = date?.getDate() || 0;
    const start = startHour;
    const end = endHour;
    start?.setFullYear(year, month, day);
    end?.setFullYear(year, month, day);
    return {
        start,
        end,
    };
};

export const weekdaysTranslations = (
    intl: IntlShape,
): Record<string, string> => ({
    monday: intl.formatMessage({ id: 'week.monday', defaultMessage: 'Lundi' }),
    tuesday: intl.formatMessage({
        id: 'week.tuesday',
        defaultMessage: 'Mardi',
    }),
    wednesday: intl.formatMessage({
        id: 'week.wednesday',
        defaultMessage: 'Mercredi',
    }),
    thursday: intl.formatMessage({
        id: 'week.thursday',
        defaultMessage: 'Jeudi',
    }),
    friday: intl.formatMessage({
        id: 'week.friday',
        defaultMessage: 'Vendredi',
    }),
    saturday: intl.formatMessage({
        id: 'week.saturday',
        defaultMessage: 'Samedi',
    }),
    sunday: intl.formatMessage({
        id: 'week.sunday',
        defaultMessage: 'Dimanche',
    }),
});

export const formatDateToFullText = (date: Date): string =>
    format(date, 'iii d MMMM', localeConfig);

export const formatDateToShortText = (date: Date): string =>
    format(date, 'd MMM', localeConfig);

export const generateArrayOfNFollowingDays = (
    date: Date,
    repetitions: number,
): Date[] =>
    Array(repetitions)
        .fill(null)
        .map((_, i) => add(date, { days: i + 1 }));

export const getStartOfWeek = (date: Date): Date =>
    startOfWeek(date, localeConfig);

export const generateWeek = (date: Date): Date[] =>
    Array.from({ length: 7 }, (_, index) =>
        addDays(getStartOfWeek(date), index),
    );

export const getDatesBasedOnView = (
    view: AgendaSettings['desktopView'] | AgendaSettings['mobileView'],
    date: Date,
): Date[] => {
    if (view === 'threeDay') {
        return [date, ...generateArrayOfNFollowingDays(date, 2)];
    }
    if (view === 'week') {
        return generateWeek(date);
    }
    return [date];
};

export const getArrayOfDaysNotPresent = (dates: Date[]): number[] => {
    const days = dates.map((date) => date.getDay());
    return [...Array(7).keys()].reduce((acc, day) => {
        if (days.includes(day)) {
            return acc;
        }
        return [...acc, day];
    }, [] as number[]);
};

export const formatDateToFulltext = (date: Date): string =>
    // eslint-disable-next-line quotes
    format(date, "iiii d MMMM à HH'h'mm", localeConfig);

export const getContextDate = (
    creationContextDate?: Date,
    selectedDate?: Date,
): Date => {
    if (creationContextDate) {
        return creationContextDate;
    }

    return selectedDate
        ? setDate(new Date(), selectedDate.getDate())
        : new Date();
};

const previousDayMs = Date.now() - 1000 * 60 * 60 * 24;
export const isPassCurrentDate = (dateToCompared: Date): boolean =>
    !isBefore(new Date(previousDayMs), dateToCompared);
